import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateMockData } from '../data/mockData';
import { delay } from '../utils/helpers';

const AuthContext = createContext();

const USERS = [
  {
    email: 'admin@rdgarantia.com',
    password: 'Admin123!',
    role: 'admin',
    nombre: 'Admin Sistema',
    avatar: null
  },
  {
    email: 'agente@rdgarantia.com',
    password: 'Agente123!',
    role: 'agente',
    nombre: 'Carlos Agente',
    avatar: null
  },
  {
    email: 'vendedor@rdgarantia.com',
    password: 'Vendedor123!',
    role: 'vendedor',
    nombre: 'María Vendedor',
    avatar: null
  },
  {
    email: 'tecnico@rdgarantia.com',
    password: 'Tecnico123!',
    role: 'tecnico',
    nombre: 'Juan Técnico',
    avatar: null
  },
  {
    email: 'cliente@rdgarantia.com',
    password: 'Cliente123!',
    role: 'cliente',
    nombre: 'Juan Pérez García',
    avatar: null
  }
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mockData, setMockData] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('eureka_user');
    const storedToken = localStorage.getItem('eureka_token');

    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
    }

    const storedData = localStorage.getItem('eureka_mock_data');
    if (storedData) {
      const parsedData = JSON.parse(storedData);

      // Validar que los datos tengan la estructura correcta con clientes
      if (!parsedData.clientes || !parsedData.notificacionesCliente || !parsedData.documentosCliente) {
        console.warn('Datos mock antiguos detectados. Regenerando...');
        localStorage.removeItem('eureka_mock_data');
        const data = generateMockData();
        setMockData(data);
        localStorage.setItem('eureka_mock_data', JSON.stringify(data));
      } else {
        setMockData(parsedData);
      }
    } else {
      const data = generateMockData();
      setMockData(data);
      localStorage.setItem('eureka_mock_data', JSON.stringify(data));
    }

    setLoading(false);
  }, []);

  const login = async (email, password) => {
    await delay(800);
    
    const foundUser = USERS.find(u => u.email === email && u.password === password);
    
    if (!foundUser) {
      throw new Error('Credenciales incorrectas');
    }

    const token = `mock-jwt-token-${Date.now()}`;
    const userData = {
      email: foundUser.email,
      role: foundUser.role,
      nombre: foundUser.nombre,
      avatar: foundUser.avatar
    };

    localStorage.setItem('eureka_user', JSON.stringify(userData));
    localStorage.setItem('eureka_token', token);
    
    const auditLog = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      usuario: email,
      accion: 'login',
      entidad: 'sistema',
      entidadId: null,
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      userAgent: navigator.userAgent,
      detalles: 'Inicio de sesión exitoso'
    };
    
    const updatedData = {
      ...mockData,
      auditLogs: [auditLog, ...mockData.auditLogs]
    };
    setMockData(updatedData);
    localStorage.setItem('eureka_mock_data', JSON.stringify(updatedData));
    
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    await delay(300);
    
    if (user) {
      const auditLog = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString(),
        usuario: user.email,
        accion: 'logout',
        entidad: 'sistema',
        entidadId: null,
        ip: '192.168.1.' + Math.floor(Math.random() * 255),
        userAgent: navigator.userAgent,
        detalles: 'Cierre de sesión'
      };
      
      const updatedData = {
        ...mockData,
        auditLogs: [auditLog, ...mockData.auditLogs]
      };
      setMockData(updatedData);
      localStorage.setItem('eureka_mock_data', JSON.stringify(updatedData));
    }
    
    localStorage.removeItem('eureka_user');
    localStorage.removeItem('eureka_token');
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;

    const permissions = {
      admin: ['all'],
      agente: ['view_all_garantias', 'view_all_averias', 'view_reports', 'manage_vendedores'],
      vendedor: ['create_garantia', 'view_own_garantias', 'view_own_averias', 'view_own_reports'],
      tecnico: ['view_averias', 'authorize_averia', 'reject_averia', 'view_garantias']
    };

    const userPermissions = permissions[user.role] || [];
    return userPermissions.includes('all') || userPermissions.includes(permission);
  };

  const addAuditLog = (accion, entidad, entidadId, detalles) => {
    if (!user) return;

    const auditLog = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      usuario: user.email,
      accion,
      entidad,
      entidadId,
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      userAgent: navigator.userAgent,
      detalles
    };

    const updatedData = {
      ...mockData,
      auditLogs: [auditLog, ...mockData.auditLogs]
    };
    setMockData(updatedData);
    localStorage.setItem('eureka_mock_data', JSON.stringify(updatedData));
  };

  const updateMockData = (updates) => {
    const updatedData = { ...mockData, ...updates };
    setMockData(updatedData);
    localStorage.setItem('eureka_mock_data', JSON.stringify(updatedData));
  };

  const switchUser = async (targetUserEmail) => {
    await delay(300);

    // Find the target user in the USERS array
    const targetUser = USERS.find(u => u.email === targetUserEmail);
    if (!targetUser) {
      throw new Error('Usuario no encontrado');
    }

    // Log the switch user action
    const switchLog = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      usuario: user?.email || 'sistema',
      accion: 'switch_user',
      entidad: 'usuario',
      entidadId: targetUser.email,
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      userAgent: navigator.userAgent,
      detalles: `Cambio de rol a ${targetUser.role} - ${targetUser.nombre}`
    };

    const updatedData = {
      ...mockData,
      auditLogs: [switchLog, ...mockData.auditLogs]
    };
    setMockData(updatedData);
    localStorage.setItem('eureka_mock_data', JSON.stringify(updatedData));

    // Store original admin user for restoration
    if (user?.role === 'admin' && !localStorage.getItem('eureka_original_admin')) {
      localStorage.setItem('eureka_original_admin', JSON.stringify(user));
    }

    // Set the new user
    setUser(targetUser);
    localStorage.setItem('eureka_user', JSON.stringify(targetUser));

    return targetUser;
  };

  const restoreAdmin = async () => {
    await delay(300);

    const originalAdmin = localStorage.getItem('eureka_original_admin');
    if (!originalAdmin) {
      throw new Error('No hay administrador original para restaurar');
    }

    const adminUser = JSON.parse(originalAdmin);

    // Log the restore action
    const restoreLog = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      usuario: user?.email || 'sistema',
      accion: 'restore_admin',
      entidad: 'usuario',
      entidadId: adminUser.email,
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      userAgent: navigator.userAgent,
      detalles: `Restauración a rol administrador - ${adminUser.nombre}`
    };

    const updatedData = {
      ...mockData,
      auditLogs: [restoreLog, ...mockData.auditLogs]
    };
    setMockData(updatedData);
    localStorage.setItem('eureka_mock_data', JSON.stringify(updatedData));

    // Clear the stored original admin
    localStorage.removeItem('eureka_original_admin');

    // Restore the admin user
    setUser(adminUser);
    localStorage.setItem('eureka_user', JSON.stringify(adminUser));

    return adminUser;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    switchUser,
    restoreAdmin,
    hasPermission,
    mockData,
    updateMockData,
    addAuditLog
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
